import { useMutation } from "@apollo/client";
import { StackScreenProps } from "@react-navigation/stack";
import { Page, TextField, Button } from "@towersystems/roam-common-native";
import { TerminalClosure } from "@towersystems/roam-common/lib/generated-types";
import { useCallback, useEffect } from "react";
import { View } from "react-native-ui-lib";
import {
  CloseShiftRootScreenParamList,
  ShiftRootParamList,
} from "../../../../types";
import { useSetupAllStartupEntities } from "../../../../utilities/setup";
import { MUTATION_CLOSE_TERMINAL_SHIFT } from "../../../../utilities/terminal-shift/constants";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import {
  useCloseShiftProcessProvider,
  useEndOfShiftContextProvider,
} from "../../utils/shift/hooks";

export type CloseShiftNoteScreenProps = StackScreenProps<
  CloseShiftRootScreenParamList & ShiftRootParamList,
  "CloseShiftNoteScreen"
>;

export const CloseShiftNoteScreen = ({
  navigation,
}: CloseShiftNoteScreenProps) => {
  const { fetchAndSyncEntity } = useSetupAllStartupEntities();
  const { shiftTerminal } = useEndOfShiftContextProvider();
  const [f] = useMutation<{ closeTerminal: TerminalClosure }>(
    MUTATION_CLOSE_TERMINAL_SHIFT
  );
  const { note, setNote, payments } = useCloseShiftProcessProvider();
  const shouldFocus = useHasTransitionEnded(navigation);
  const title = "Add Note to Shift";

  const handleOnSave = useCallback(() => {
    return f({
      variables: {
        input: {
          note: note,
          nextOpeningFloat: 0,
          terminalId: shiftTerminal.id,
          payments: payments.map((p) => ({
            paymentMethodId: p.paymentMethodId,
            total: p.counted || 0,
          })),
        },
      },
    })
      .then((response) => {
        // TODO: this is a hack to get the terminal to update
        fetchAndSyncEntity("terminals");
        // need to reset the navigation state, otherwise the
        // back button will take you back to the note screen
        return navigation.getParent()?.reset({
          index: 0,
          routes: [
            { name: "ShiftLandingScreen" },
            { name: "ShiftHistoryScreen" },
            {
              name: "ClosedShiftSummaryScreen",
              params: {
                shiftId: response.data?.closeTerminal.id,
              },
            },
          ],
        });
      })
      .catch((err) => console.log(err));
  }, [note]);

  useEffect(() => {
    navigation.setOptions({
      headerTitle: title,
      headerRight: () => (
        <Button
          label="Close Shift"
          isAction
          onPress={() => handleOnSave()}
          size="compact"
        ></Button>
      ),
    });
  }, [title, handleOnSave]);

  return (
    <Page scrollable={false}>
      <View style={{ height: "100%" }} flex>
        <TextField
          textarea
          placeholder="Leave a note..."
          value={note}
          onChangeText={setNote}
          autoFocus={shouldFocus}
        />
      </View>
    </Page>
  );
};
